import { Component, signal, Input, output, resource, computed, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingPanelService } from '../shared/loading-panel/loading-panel.service';
import { GenericModalService, ModalMultiButton, ModalSettingsBase, ModalSettingsConfirmMultiButton, ModalSettingsTemplate } from '../shared/generic-modal/generic-modal.service';
import { ToastService, ToastType } from '../shared/toast/toast.service';
import { PartnerService } from './services/partner.service';
import { PartnerIdentificationTypeService } from './services/partner-identification-type.service';
import { PartnerComponentBase } from './partner-search-details.base';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule, UntypedFormGroup, AbstractControl } from '@angular/forms';
import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';
import { PartnerOnboardingService } from './services/partner-onboarding.service';
import { DocumentTypeDeveloper } from '../developer/models/document-type';
import { DeveloperService } from '../developer/services/developer.service';
import { PartnerOnboardingLogEvent } from './models/partner-onboarding-logevent';
import { IdentificationType, Partner, PartnerIdentificationType, PartnerType } from './models';
import { PartnerOnboardingDocumentType } from './models/partner-onboarding-documenttype';
import { PartnerOnboardingIdentificationType } from './models/partner-onboarding-identificationtype';
import { MultiselectboxComponent } from '../controls/components/basic/multiselectbox/multiselectbox.component';
import { CheckboxComponent } from '../controls/components/basic/checkbox/checkbox.component';
import { TextboxComponent } from '../controls/components/basic/textbox/textbox.component';
import { ButtonComponent } from '../controls/components/basic/button/button.component';
import { LayoutRowComponent } from '../controls/components/basic/layout-row/layout-row.component';
import { LayoutColumnComponent } from '../controls/components/basic/layout-column/layout-column.component';
import { ButtonPrimaryDirective } from '../controls/directives/button-primary.directive';
import { CustomValidationService } from '../controls/services/custom-validation.service';
import { PartnerOnboardingCustomValidationService } from './services/partner-onboarding-customvalidation.service';
import { ButtonTertieryDirective } from '../controls/directives/button-tertiery.directive';
import { UniqueValidatorFn } from '../controls/directives/unique-validator.directive';
import { CurrentUserService } from '../user/services';
import { Tag, TagsUsage } from '../tags/models';
import { TagsService } from '../tags/services/tags.service';
import { PartnerTagsService } from './services/partner-tags.service';
import { PartnerContactService } from './services/partner-contact.service';
import { PartnerOnboardingSendInvitationDialogComponent, PartnerOnboardingSendInvitationDialogResult } from './onboarding-details/partner-onboarding-send-invitation-dialog/partner-onboarding-send-invitation-dialog.component';
import { DocumentTypeInput, ItemStatus, PartnerName, PartnerOnboarding, PartnerOnboardingData, PartnerOnboardingInput, PartnerOnboardingOutput, PartnerOnboardingStatus, PartnerContactType as OnboardingPartnerContactType, IdentificationType as OnboardingPartnerIdentificationType, PartnerContactEmail, PartnerPhoneNumber, PartnerOnboardingItemStatus, PartnerContact, PartnerContactName, Transport } from '../partnerportal/models/partner-onboarding';
import { PartnerOnboardingPreset } from './models/partner-onboarding-preset';
import { SingleselectboxComponent } from '../controls/components/basic/singleselectbox/singleselectbox.component';
import { PresetManagementDialogComponent, PresetManagementResult } from './onboarding-setup/partner-onboarding-preset-manage-dialog/partner-onboarding-preset-manage-dialog.component';
import { PresetNameDialogComponent } from './onboarding-setup/partner-onboarding-preset-name-dialog/partner-onboarding-preset-name-dialog.component';
import { PartnerOnboardingTransportComponent } from './onboarding-setup/partner-onboarding-transport/partner-onboarding-transport.component';

@Component({
    templateUrl: './partner-search-details-onboarding.component.html',
    styleUrl: './partner-search-details-onboarding.component.scss',
    selector: 'ep-partner-details-onboarding',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, PermissionRequiredDirective, ReactiveFormsModule, MultiselectboxComponent, CheckboxComponent, TextboxComponent, ButtonComponent, ButtonPrimaryDirective, ButtonTertieryDirective, LayoutRowComponent, LayoutColumnComponent, SingleselectboxComponent, PartnerOnboardingTransportComponent]
})

export class PartnerSearchDetailsOnboardingComponent extends PartnerComponentBase {

    // Inject services
    private partnerService = inject(PartnerService);
    private toastService = inject(ToastService);
    private modalService = inject(GenericModalService);
    private loadingPanelService = inject(LoadingPanelService);
    private partnerOnboardingService = inject(PartnerOnboardingService);
    private developerService = inject(DeveloperService);
    private partnerIdentificationTypeService = inject(PartnerIdentificationTypeService);
    private fb = inject(FormBuilder);
    private currentUserService = inject(CurrentUserService);
    private tagsService = inject(TagsService);
    private partnerTagsService = inject(PartnerTagsService);
    private partnerContactService = inject(PartnerContactService);
    private router = inject(Router);

    @Input() isNewPartner: boolean = false;
    saved = output<Partner>();
    cancelled = output<void>();
    refresh = output();
    private _currentFormGroup: UntypedFormGroup | null = null;
    protected formState = signal<'initial' | 'loaded' | 'editing' | 'submitting' | 'disabled'>('initial');

    public doneLoading = signal(false);
    public partnerId = signal<number>(null);
    public forcePatch = signal(false);
    private pendingPresetId = signal<number | null>(null);

    public checkboxControlNames: string[] = [];
    public newPartner: Partner = null;
    public selectedOnboardingPreset = signal<PartnerOnboardingPreset | null>(null);
    private originalPresetId = signal<number | null>(null);

    /* -------------------------------------------------------------------------- */
    /*                                  Resources                                 */
    /* -------------------------------------------------------------------------- */

    identificationTypesResource = resource({
        params: () => ({}),
        loader: () => this.partnerIdentificationTypeService.getIdentificationTypes(false),
      });

    documentTypesResource = resource({
        params: () => ({}),
        loader: () => this.developerService.getDocumentTypes(),
      });

    formatsResource = resource({
        params: () => ({}),
        loader: () => this.developerService.getFormats(),
      });

    docConfigsResource = resource({
        params: () => ({}),
        loader: () => this.developerService.getDocumentConfigs(),
      });

    partnerOnboardingResource = resource({
        params: () => ({partnerId: this.partnerId()}),
        loader: ({params}) => this.getOnboardingPartner(params as {partnerId: number}),
    });

    partnerOnboardingPresetsResource = resource({
        params: () => ({}),
        loader: () => this.partnerOnboardingService.getPartnerOnboardingPresets(),
    });

    partnerTypesResource = resource({
    params: () => this.isNewPartner ? {} : null,
    loader: () => this.partnerService.getPartnerTypes(),
    });

    allTagsResource = resource({
        params: () => this.isNewPartner ? {} : null,
        loader: () => this.tagsService.getTags(TagsUsage.Partner),
    });

    contactTypesResource = resource({
        params: () => this.isNewPartner ? {} : null,
        loader: () => this.partnerContactService.getContactTypes(),
    });




    /* -------------------------------------------------------------------------- */
    /*                              Computed Signals                              */
    /* -------------------------------------------------------------------------- */

    identificationTypes = computed(() => this.identificationTypesResource.value() );
    documentTypes = computed(() => this.documentTypesResource.value() );
    docConfigs = computed(() => this.docConfigsResource.value() );
    partnerOnboarding = signal<PartnerOnboarding>(null); // Not computed to allow update based on preset change
    onboardingPresets = computed(() => this.partnerOnboardingPresetsResource.value() ?? []);
    allTags = computed(() => this.allTagsResource.value() ?? []);
    contactTypes = computed(() => this.contactTypesResource.value() ?? []);
    hasEditMailTemplatePermission = computed(() => this.currentUserService.hasPermission('Mail_View_Templates'));
    formats = computed(() => {

        const formats = this.formatsResource.value();
        return formats?.sort((a, b) => a.FormatName.localeCompare(b.FormatName));
    });
    partnerTypes = computed(() => {
        const types = this.partnerTypesResource.value();
        if (!types || !this.isNewPartner) return [];

        return this.currentUserService.hasPermission('Partner_Action_InternalPartner')
            ? types
            : types.filter(e => e.Name.toLowerCase() !== 'internal');
    });

    identificationTypesWrapper = computed(() => {
        const identificationTypes = this.identificationTypes();
        const partnerOnboarding = this.partnerOnboarding();

        if (!identificationTypes || !partnerOnboarding) {
            return [];
        }

        return identificationTypes.map(idType => {
            const existsInInput = partnerOnboarding.Data.Input.IdentificationTypes
                .some(inputIdType => inputIdType.Id === idType.IdentificationTypeId);
            return {
                IdentificationType: idType,
                Selected: existsInInput
            } as PartnerOnboardingIdentificationType;
        });
    });

    documentTypesInWrapper = computed(() => {
        const documentTypes = this.documentTypes();
        const partnerOnboarding = this.partnerOnboarding();
        const docConfigs = this.docConfigs();
        const formats = this.formats();

        if (!documentTypes || !partnerOnboarding || !docConfigs || !formats) {
            return [];
        }

        return this.createDocumentTypeWrapper(
            partnerOnboarding.Data.Input.DocumentTypesIn,
            'in'
        );
    });

    documentTypesOutWrapper = computed(() => {
        const documentTypes = this.documentTypes();
        const partnerOnboarding = this.partnerOnboarding();
        const docConfigs = this.docConfigs();
        const formats = this.formats();

        if (!documentTypes || !partnerOnboarding || !docConfigs || !formats) {
            return [];
        }

        return this.createDocumentTypeWrapper(
            partnerOnboarding.Data.Input.DocumentTypesOut,
            'out'
        );
    });
    reactiveFormGroup = computed((): UntypedFormGroup => {
        // During submitting, return the existing form
        if (this.formState() === 'submitting' && this._currentFormGroup) {
            return this._currentFormGroup;
        }
        const documentTypesInWrapper = this.documentTypesInWrapper();
        const documentTypesOutWrapper = this.documentTypesOutWrapper();
        const partnerOnboarding = this.partnerOnboarding();
        const docConfigs = this.docConfigs();
        const formats = this.formats();
        const idtypesWrapper = this.identificationTypesWrapper();
        const partnerOnboardingPreset = this.onboardingPresets();

        // Only compute if all dependencies are loaded
        if (!partnerOnboarding || !docConfigs || !formats || !idtypesWrapper || !partnerOnboardingPreset || !documentTypesInWrapper || !documentTypesOutWrapper) {
            return this.fb.group({});
        }
        const formgroup = this.fb.group({},{validators: [PartnerOnboardingCustomValidationService.anySelected(this.checkboxControlNames)]});
        // Document Types
        this.createDocTypeFormControls('in', this.documentTypesInWrapper(), formgroup);
        this.createDocTypeFormControls('out', this.documentTypesOutWrapper(), formgroup);

        // Identification Types
        this.identificationTypesWrapper().forEach(idType => {
            const controlNameCheckbox = `idtypecheckbox_${idType.IdentificationType.IdentificationTypeId}`;
            formgroup.addControl(controlNameCheckbox, this.fb.control(idType.Selected ?? false));
        });

        // Partner contact
        formgroup.addControl('email', this.fb.control({ value: this.partnerOnboarding().Data.Output.Contact.Email.ContactEmail, disabled: false }, [Validators.email, Validators.required]));

        // Contact contact type
        formgroup.addControl('contacttypes', this.fb.control({ value: this.partnerOnboarding().Data.Output.Contact.Types?.map(ct => ct.Id) ?? [], disabled: false }, [Validators.required]));

        // NotificationEmailAddress
        formgroup.addControl('notificationemail', this.fb.control({ value: this.partnerOnboarding().NotificationEmailAddress, disabled: false }, [Validators.email]));


        //Preset Dropdown
        const initialPresetValue = this.selectedOnboardingPreset()?.Id || null;
        formgroup.addControl('onboardingPresetsControl', this.fb.control({
            value: initialPresetValue,
            disabled: this.formState() !== 'editing'
        }));

        // Partner name
        if(this.isNewPartner){
            formgroup.addControl('partnername', this.fb.control({ value: this.newPartner?.PartnerName, disabled: this.isNewPartner }, [Validators.required]));
        }
        else {
            formgroup.addControl('partnername', this.fb.control({ value: this.selectedPartner?.PartnerName, disabled: this.isNewPartner }));
        }

        // Supported Transports
        formgroup.addControl('supportedTransports', this.fb.control({ value: this.partnerOnboarding().Data.Input.SupportedTransports || [], disabled: false }, []));

        this._currentFormGroup = formgroup;
        return formgroup;
    });


    /* -------------------------------------------------------------------------- */
    /*                             Page Initialization                            */
    /* -------------------------------------------------------------------------- */

    async partnerSet(): Promise<unknown> {
        this.partnerId.set(this.selectedPartner?.PartnersId);
        this.partnerOnboardingResource.reload();
        return Promise.resolve();
    }



    constructor() {
        super();
        effect(() => this.handleLoadingState());
        effect(() => this.patchFormData());
        effect(() => this.initializeNewPartner());
        effect(() => this.handleResourceData());
        effect(() => this.handlePendingPresetSelection());
    }


    /* -------------------------------------------------------------------------- */
    /*                                   Effects                                  */
    /* -------------------------------------------------------------------------- */

    private handleLoadingState(): void {
        const isLoading = this.partnerOnboardingResource.isLoading()
            || this.identificationTypesResource.isLoading()
            || this.documentTypesResource.isLoading()
            || this.formatsResource.isLoading()
            || this.docConfigsResource.isLoading()
            || this.partnerOnboardingPresetsResource.isLoading()
            || (this.isNewPartner && (
                this.partnerTypesResource.isLoading()
                || this.allTagsResource.isLoading()
                || this.contactTypesResource.isLoading()
            ));

        if (isLoading) {
            this.doneLoading.set(false);
            this.loadingPanelService.showLoadingPanelDirect('Loading...');
        } else {
            this.doneLoading.set(true);
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    private handleResourceData(): void {
        const resourceData = this.partnerOnboardingResource.value();
        if (resourceData) {
            this.partnerOnboarding.set(resourceData);
            if (this.formState() === 'initial') {
                this.formState.set('loaded');
            }
        }
    }

    private patchFormData(): void {
        const forcePatch = this.forcePatch();
        if (forcePatch) {
            this.forcePatch.set(false);
        }
        else if (this.formState() === 'submitting' || this.formState() === 'editing') {
            return;
        }
        const formGroup = this.reactiveFormGroup();
        const documentTypesIn = this.documentTypesInWrapper();
        const documentTypesOut = this.documentTypesOutWrapper();
        const identificationTypes = this.identificationTypesWrapper();
        const partnerOnboarding = this.partnerOnboarding();
        const partnerOnboardingPresets = this.onboardingPresets();

        if (!formGroup || !documentTypesIn || !documentTypesOut || !identificationTypes || !partnerOnboarding || !partnerOnboardingPresets) {
            return;
        }

        this.patchDocumentTypes('in', documentTypesIn, formGroup);
        this.patchDocumentTypes('out', documentTypesOut, formGroup);
        this.patchIdentificationTypes(identificationTypes, formGroup);
        this.patchEmailFields(partnerOnboarding, formGroup);
        this.patchPartnerName(formGroup);
        this.patchSupportedTransports(partnerOnboarding, formGroup);

        if (this.formState() === 'loaded') {
            formGroup.disable();
            this.formState.set('disabled');
        }
        else if(this.formState() !== 'editing'){
            formGroup.disable();
            this.formState.set('disabled');
        }

    }

    // Helper method for form patching
    private patchDocumentTypes(direction: 'in' | 'out', documentTypes: PartnerOnboardingDocumentType[], formGroup: UntypedFormGroup): void {
        documentTypes.forEach(docType => {
            const controlNameFormat = `doctype${direction}format_${docType.DocumentType.Id}`;
            const controlFormat = formGroup.get(controlNameFormat);
            if (controlFormat) {
                controlFormat.patchValue(docType.SelectedFormats ?? []);
            }

            const controlNameCheckbox = `doctype${direction}checkbox_${docType.DocumentType.Id}`;
            const controlCheckbox = formGroup.get(controlNameCheckbox);
            if (controlCheckbox) {
                controlCheckbox.patchValue(docType.Selected ?? false);
            }
        });
    }

    // Helper method for form patching
    private patchIdentificationTypes(identificationTypes: PartnerOnboardingIdentificationType[], formGroup: UntypedFormGroup): void {
        identificationTypes.forEach(idType => {
            const controlNameCheckbox = `idtypecheckbox_${idType.IdentificationType.IdentificationTypeId}`;
            const controlCheckbox = formGroup.get(controlNameCheckbox);
            if (controlCheckbox) {
                controlCheckbox.patchValue(idType.Selected ?? false);
            }
        });
    }

    // Helper method for form patching
    private patchEmailFields(partnerOnboarding: PartnerOnboarding, formGroup: UntypedFormGroup): void {
        const controlEmail = formGroup.get('email');
        if (controlEmail) {
            controlEmail.patchValue(partnerOnboarding.Data.Output.Contact.Email.ContactEmail);
        }

        const controlContactTypes = formGroup.get('contacttypes');
        if (controlContactTypes) {

            const contactTypes = partnerOnboarding.Data.Output.Contact.Types;
            controlContactTypes.patchValue(contactTypes?.map(ct => ct.Id) ?? []);
        }
    }

    // Helper method for form patching
    private patchPartnerName(formGroup: UntypedFormGroup): void {
        const controlPartnerName = formGroup.get('partnername');
        if (controlPartnerName && this.newPartner?.PartnerName) {
            controlPartnerName.patchValue(this.newPartner.PartnerName);
        }
    }

    // Helper method for form patching
    private patchSupportedTransports(partnerOnboarding: PartnerOnboarding, formGroup: UntypedFormGroup): void {
        const controlSupportedTransports = formGroup.get('supportedTransports');
        if (controlSupportedTransports) { 
            controlSupportedTransports.patchValue(partnerOnboarding.Data.Input.SupportedTransports);
        }
    }

    
    private initializeNewPartner(): void {
        if (!this.isNewPartner) return;

        const partnerTypes = this.partnerTypes();
        const identificationTypes = this.identificationTypes();

        if (!partnerTypes || !identificationTypes) {
            return;
        }

        this.initializeNewPartnerFromSignals(partnerTypes, identificationTypes);
        this.formState.set('editing');
        this.reactiveFormEdit();
    }

    // Helper Method for initializeNewPartner()
    private initializeNewPartnerFromSignals(partnerTypes: PartnerType[], identificationTypes: IdentificationType[]): void {
        this.newPartner = new Partner();
        this.newPartner.PartnerType = partnerTypes.length
            ? partnerTypes.find(e => e.Name.toLowerCase() === 'external')
            : { Id: undefined, Name: '' } as PartnerType;

        this.newPartner.MainIdentificationType = new PartnerIdentificationType();
        this.newPartner.MainIdentificationType.IdentificationType = identificationTypes.length
            ? identificationTypes[0]
            : new IdentificationType();
        this.newPartner.MainIdentificationType.IsMainIdentification = true;
        this.newPartner.MainIdentificationType.Value = '';
        this.newPartner.IsProxyPartner = false;
    }




    // Helper Method for LoadSelectedDocumentTypes()
    createDocumentTypeWrapper(docTypesInput: DocumentTypeInput[], direction: string): PartnerOnboardingDocumentType[] {
        return this.documentTypes().map(docType => {
            const existsInOutput = docTypesInput.some(outputDocType => outputDocType.Id === docType.Id);
            const displayName = this.getDocumentTypeDisplayName(docType, docTypesInput);
            const availableFormats = this.getFormatsFromDocumentConfigs(docType,direction);
            const wrapperFormats = this.getSelectedFormatsForDocumentType(docType, docTypesInput);
            const selectedFormats = wrapperFormats.map(format => format.FormatsId);
            return {
                DocumentType: docType,
                DisplayName: displayName ?? docType.Name,
                Selected: existsInOutput,
                AvailableFormats: availableFormats,
                Formats: wrapperFormats,
                SelectedFormats: selectedFormats
            } as PartnerOnboardingDocumentType;
        });
    }

    // Helper Method for createDocumentTypeWrapper()
    private getSelectedFormatsForDocumentType(docType: DocumentTypeDeveloper, docTypesInput: DocumentTypeInput[]) {
        const existsInInput = docTypesInput.some(inputDocType => inputDocType.Id === docType.Id);
        return existsInInput
            ? this.formats().filter(format => docTypesInput
                .find(inputDocType => inputDocType.Id === docType.Id)
                .Formats.some(partnerFormat => partnerFormat.Id === format.FormatsId)
            )
            : [];
    }

    // Helper Method for createDocumentTypeWrapper()
    private getFormatsFromDocumentConfigs(docType: DocumentTypeDeveloper, direction: string) {
        const availableFormatIds = this.docConfigs()
            .filter(config => config.DistributionObjectId === docType.Id && config.Direction.toLowerCase() === direction.toLowerCase())
            .map(config => config.FormatId);
        const availableFormats = this.formats().filter(format => availableFormatIds.includes(format.FormatsId)) ?? [];
        return availableFormats;
    }

    // Helper Method for createDocumentTypeWrapper()
    private getDocumentTypeDisplayName(docType: DocumentTypeDeveloper, docTypesInput: DocumentTypeInput[]): string {
        let displayName = docTypesInput.find(inputDocType => inputDocType.Id === docType.Id)?.Name;
        if (displayName?.trim().length === 0) {
            displayName = docType.Name;
        }
        return displayName;
    }

    async getOnboardingPartner(request: {partnerId: number}): Promise<PartnerOnboarding> {
        if (request.partnerId) {
            const existingOnboarding = await this.partnerOnboardingService.getPartnerOnboarding(request.partnerId);
            if (existingOnboarding) {
                    return existingOnboarding;
                }
        }
        return this.createNewOnboarding(request.partnerId);
    }

    // Helper for getOnboardingPartner()
    private createNewOnboarding(partnerId: number | null = null): PartnerOnboarding {
        const partnerDataInput = {
            IdentificationTypes: [],
            DocumentTypesIn: [],
            DocumentTypesOut: []
        } as PartnerOnboardingInput;

        const partnerDataOutput = this.createNewParterDataOutput();
        const partnerOnboardingData = {
            Input: partnerDataInput,
            Output: partnerDataOutput
        } as PartnerOnboardingData;

        return {
            Id: null,
            PartnerOnboardingGuid: null,
            PartnerId: partnerId,
            NotificationEmailAddress: '',
            Status: 'InProgress',
            Data: partnerOnboardingData
        } as PartnerOnboarding;
    }

    private createNewParterDataOutput() {
        return {
            Name: new PartnerName(),
            Contact: new PartnerContact( {Name: new PartnerContactName(), Email: new PartnerContactEmail(), PhoneNumber: new PartnerPhoneNumber()} ),
            Identifications: [],
            DocumentTypesIn: [],
            DocumentTypesOut: []
        } as PartnerOnboardingOutput;
    }



    // Helper Method for reactiveFormGroup()
    createDocTypeFormControls(direction: string, wrappers: PartnerOnboardingDocumentType[], fb: FormGroup<Record<string, AbstractControl>>) : FormGroup<Record<string, AbstractControl>> {
        wrappers.forEach(docType => {
            const controlNameCheckbox = `doctype${direction}checkbox_${docType.DocumentType.Id}`;
            fb.addControl(controlNameCheckbox, this.fb.control(docType.Selected ?? false));

            const controlNameDisplayName = `doctype${direction}displayname_${docType.DocumentType.Id}`;
            fb.addControl(controlNameDisplayName, this.fb.control(docType.DisplayName ?? docType.DocumentType.Name, Validators.required));

            // Formats required only if document type is selected
            const controlNameFormat = `doctype${direction}format_${docType.DocumentType.Id}`;
            fb.addControl(controlNameFormat, this.fb.control(docType.SelectedFormats ?? [],[CustomValidationService.conditionalValidator(() =>
                fb?.get(controlNameCheckbox).value === true, Validators.required)]));

            // Update format validation when checkbox changes
            fb.get(controlNameCheckbox).valueChanges.subscribe(() => {
                fb.get(controlNameFormat).updateValueAndValidity({ onlySelf: true });
            });
            this.checkboxControlNames.push(controlNameCheckbox);
        });

        return fb;
    }


    async reactiveFormSubmit(): Promise<void> {
        this.formState.set('submitting');

        // Get the updated onboarding data from form
        let updatedOnboarding: PartnerOnboarding = null;

        if(this.isNewPartner){
            // Create new partner if needed
            try {
                this.loadingPanelService.showLoadingPanelDirect('Creating partner...');
                const controlPartnerName = this.reactiveFormGroup().get('partnername');
                const partnerName = controlPartnerName.value as string;
                if (await this.partnerService.isPartnerNameAvailable(partnerName)) {
                    await this.createNewPartner(partnerName);
                }
                else {
                    this.toastService.showToast('Partner name is already used!', ToastType.error);
                    return;
                }

                updatedOnboarding = this.loadFormValues();
            }
            catch {
                this.toastService.showToast('Failed to create partner!', ToastType.error);
                return;
            }
            finally {
                this.loadingPanelService.hideLoadingPanel();
            }
        }
        else {
            // Partner exists - if the onboarding was previously completed, confirm if user wants to reopen it
            updatedOnboarding = this.loadFormValues();
            if (updatedOnboarding.Status === PartnerOnboardingStatus.PartnerDone || updatedOnboarding.Status === PartnerOnboardingStatus.Done) {
                const settings = {} as ModalSettingsBase;
                settings.size = 'lg';
                settings.backdrop = 'static';
                settings.windowClass = 'ngb-modal-on-top';
                settings.backdropClass = 'ngb-modal-on-top-backdrop';

                const confirmMultiButtonSettings = new ModalSettingsConfirmMultiButton();
                Object.assign(confirmMultiButtonSettings, settings);
                confirmMultiButtonSettings.body = 'You have made changes to a previously completed onboarding.<br/>Would you like to reopen the onboarding process and let the partner provide new information?';
                confirmMultiButtonSettings.title = 'Reopen partner onboarding';
                confirmMultiButtonSettings.buttons = [
                    new ModalMultiButton('Save - and reopen the onboarding', 'open', 'btn btn-primary'),
                    new ModalMultiButton('Save - but keep the onboarding closed', 'doNotOpen', 'btn btn-secondary'),
                    new ModalMultiButton('Cancel', 'cancel', 'btn btn-link')
                ];

                let cancelled = false;
                await this.modalService.confirmMultiButton(confirmMultiButtonSettings).then(result => {
                    if (result === 'open') {
                        // Reopen onboarding
                        updatedOnboarding.Status = PartnerOnboardingStatus.InProgress;
                        updatedOnboarding.Data.Output = this.updateFieldStatus(updatedOnboarding.Data.Output);
                    }
                    else if (result === 'doNotOpen') {
                        // Keep the status as Done/PartnerDone, but update the fields
                    }
                    else if (result === 'cancel') {
                        // Cancel save
                        this.formState.set('submitting');
                        cancelled = true;
                    }
                });

                if (cancelled) {
                    return;
                }
            }
        }

        // Save Model to DB
        try {
            this.loadingPanelService.showLoadingPanelDirect('Creating onboarding...');

            // Use the returned data to build the final onboarding data
            const partnerOnboardingData = {
                Input: updatedOnboarding.Data.Input,
                Output: updatedOnboarding.Data.Output
            };

            // Update the current onboarding with the form data
            const finalOnboarding = structuredClone(this.partnerOnboarding());
            finalOnboarding.Data = partnerOnboardingData;
            finalOnboarding.Status = updatedOnboarding.Status;
            finalOnboarding.NotificationEmailAddress = updatedOnboarding.NotificationEmailAddress;

            if(finalOnboarding.Id && finalOnboarding.Id > 0){
                const savedPartnerOnboarding = await this.partnerOnboardingService.savePartnerOnboarding(finalOnboarding);
                this.partnerOnboarding.set(savedPartnerOnboarding);
                await this.saveLogEventPartnerOnboardingId('CustomerSave', savedPartnerOnboarding.Id);
            }
            else {
                const savedPartnerOnboarding = await this.partnerOnboardingService.savePartnerOnboarding(finalOnboarding);
                this.partnerOnboarding.set(savedPartnerOnboarding);
                await this.saveLogEventPartnerOnboardingId('CustomerStart', savedPartnerOnboarding.Id);
            }
        }
        catch {
            this.toastService.showToast('Failed to create onboarding!', ToastType.error);
        }

        this.formState.set('disabled');
        this.setEditMode(false);
        this.reactiveFormGroup().disable();

        if(this.isNewPartner){
            this.saved.emit(this.selectedPartner);
        }
        else {
            this.refresh.emit();
        }

        this.loadingPanelService.hideLoadingPanel();
    }

    // Update status for PartnerOnboarding Data Output fields
    updateFieldStatus(data: PartnerOnboardingOutput): PartnerOnboardingOutput {
        data.Name.Status.Status = data.Name.Status.Status === ItemStatus.Accepted ? ItemStatus.Accepted : ItemStatus.Open;
        data.Contact.Email.Status.Status = data.Contact.Email.Status.Status === ItemStatus.Accepted ? ItemStatus.Accepted : ItemStatus.Open;
        data.Contact.PhoneNumber.Status.Status = data.Contact.PhoneNumber.Status.Status === ItemStatus.Accepted ? ItemStatus.Accepted : ItemStatus.Open;
        data.Contact.Name.Status.Status = data.Contact.Name.Status.Status === ItemStatus.Accepted ? ItemStatus.Accepted : ItemStatus.Open;

        data.Identifications.forEach(identification => {
            identification.Status.Status = identification.Status.Status === ItemStatus.Accepted ? ItemStatus.Accepted : ItemStatus.Open;
        });

        data.DocumentTypesIn.forEach(docType => {
            docType.Status.Status = docType.Status.Status === ItemStatus.Accepted ? ItemStatus.Accepted : ItemStatus.Open;
        });

        data.DocumentTypesOut.forEach(docType => {
            docType.Status.Status = docType.Status.Status === ItemStatus.Accepted ? ItemStatus.Accepted : ItemStatus.Open;
        });

        return data;
    }

    reactiveFormEdit() {
        // Capture original preset ID. Used to restore on cancel
        const presetsControl = this.reactiveFormGroup().get('onboardingPresetsControl') as AbstractControl<number | null>;
        const currentPresetId = presetsControl?.value;
        this.originalPresetId.set(currentPresetId);

        // Set the form control to the currently selected preset
        const currentPreset = this.selectedOnboardingPreset();
        if (currentPreset && presetsControl) {
            presetsControl.patchValue(currentPreset.Id);
        }

        this.formState.set('editing');
        this.reactiveFormGroup().enable();
        this.setEditMode(true);
    }

    reactiveFormCancel() {
        // Restore original preset selection in the form control
        const originalId = this.originalPresetId();
        const presetsControl = this.reactiveFormGroup().get('onboardingPresetsControl');

        if (presetsControl) {
            presetsControl.patchValue(originalId);
        }

        // Update the selectedOnboardingPreset reference
        if (originalId !== null) {
            const originalPreset = this.onboardingPresets().find(preset => preset.Id === originalId);
            this.selectedOnboardingPreset.set(originalPreset || null);
        } else {
            this.selectedOnboardingPreset.set(null);
        }

        this.formState.set('loaded');
        this.patchFormData();
        this.reactiveFormGroup().markAsUntouched();
        this.reactiveFormGroup().markAsPristine();
        this.reactiveFormGroup().disable();
        this.setEditMode(false);
        if(this.isNewPartner){
            this.cancelled.emit();
        }
    }

    private async createNewPartner(partnerName: string) {
        if (partnerName) {
            this.newPartner.PartnerName = partnerName;
            this.newPartner.MainIdentificationType.Value = partnerName;
        }
        let partnerIdType = this.identificationTypes().find(idType => idType.Name.toLowerCase() === 'mutually defined');
        if (!partnerIdType) {
            const newIdType = { Name: 'Mutually Defined', Description: 'Mutually Defined' } as IdentificationType;
            partnerIdType = await this.partnerIdentificationTypeService.saveIdentificationType(newIdType);
        }
        this.newPartner.MainIdentificationType.IdentificationType = partnerIdType;

        this.newPartner.EdiConfig.Qualifier = this.newPartner.MainIdentificationType.IdentificationType.DefaultQualifierEdifact;
        this.newPartner.EdiConfig.PartnerIdentificationType = this.newPartner.MainIdentificationType;
        this.newPartner.X12Config.Qualifier = this.newPartner.MainIdentificationType.IdentificationType.DefaultQualifierX12;
        this.newPartner.X12Config.PartnerIdentificationType = this.newPartner.MainIdentificationType;

        this.selectedPartner = await this.partnerService.savePartner(this.newPartner);
        this.partnerOnboarding().PartnerId = this.selectedPartner.PartnersId;
        this.partnerOnboarding().Data.Output.Name = new PartnerName({Name: this.newPartner.PartnerName, Status: {Status: ItemStatus.Open, CreatedEntities: []}});

        let onboardingTag = this.allTags().find(tag => tag.Name.toLowerCase() === 'onboarding');
        if (!onboardingTag) {
            const newTag = {Name: 'Onboarding', Usage: 'Partner'} as Tag;
            onboardingTag = await this.tagsService.saveTag(newTag);
        }
        if (onboardingTag) {
            void this.partnerTagsService.setAllPartnerTags(this.selectedPartner.PartnersId, [onboardingTag.Id]);
        }
    }


    private loadFormValues(): PartnerOnboarding {
        // Get the updated onboarding data after document types are processed
        const updatedOnboarding = structuredClone(this.partnerOnboarding());

        // Update document types
        updatedOnboarding.Data.Input.DocumentTypesIn = this.loadDocumentTypeFormValues('in', this.documentTypesInWrapper());
        updatedOnboarding.Data.Input.DocumentTypesOut = this.loadDocumentTypeFormValues('out', this.documentTypesOutWrapper());

        // Update identification types by reading from form
        const selectedIdTypes: OnboardingPartnerIdentificationType[] = [];
        this.identificationTypesWrapper().forEach(idType => {
            const controlNameCheckbox = `idtypecheckbox_${idType.IdentificationType.IdentificationTypeId}`;
            const controlCheckbox = this.reactiveFormGroup().get(controlNameCheckbox);

            if (controlCheckbox && controlCheckbox.value === true) {
                selectedIdTypes.push({
                    Name: idType.IdentificationType.Name,
                    Id: idType.IdentificationType.IdentificationTypeId
                });
            }
        });

        // Update identification types
        updatedOnboarding.Data.Input.IdentificationTypes = selectedIdTypes;

        // Update contact types
        const controlContactTypes = this.reactiveFormGroup().get('contacttypes');
        const selectedContactTypes = controlContactTypes ? (controlContactTypes.value as number[]).map(id => {
            const contactType = this.contactTypes().find(ct => ct.Id === id);
            return contactType ? { Id: contactType.Id, Name: contactType.Name } as OnboardingPartnerContactType : { Id: id, Name: '' } as OnboardingPartnerContactType;
        }) : [];
        updatedOnboarding.Data.Input.DefaultContactTypes = selectedContactTypes;

        // Email
        const controlEmail = this.reactiveFormGroup().get('email');
        if (controlEmail && controlContactTypes) {
            const contactEmail = controlEmail.value as string;
            if (updatedOnboarding.Data.Output.Contact) {
                if (updatedOnboarding.Data.Output.Contact.Email) {
                    updatedOnboarding.Data.Output.Contact.Email.ContactEmail = contactEmail;
                    updatedOnboarding.Data.Output.Contact.Types = selectedContactTypes;
                }
                else {
                    updatedOnboarding.Data.Output.Contact.Email = new PartnerContactEmail({ ContactEmail: contactEmail, Status: { Status: ItemStatus.Open, CreatedEntities: [] } });
                    updatedOnboarding.Data.Output.Contact.Types = selectedContactTypes;
                }
            }
            else {
                updatedOnboarding.Data.Output.Contact = new PartnerContact({
                    Name : new PartnerContactName(),
                    Email: new PartnerContactEmail({ ContactEmail: contactEmail, Status: { Status: ItemStatus.Open, CreatedEntities: [] } }),
                    PhoneNumber: new PartnerPhoneNumber(),
                    Types: selectedContactTypes
                });
            }
        }

        //Partner Name
        const controlPartnerName = this.reactiveFormGroup().get('partnername');
        if (controlPartnerName) {
            const partnerName = controlPartnerName.value as string;
            if (updatedOnboarding.Data.Output.Name) {
                updatedOnboarding.Data.Output.Name.Name = partnerName;
            } else {
                updatedOnboarding.Data.Output.Name = { Name: partnerName, Status: { Status: ItemStatus.Open, CreatedEntities: [] } } as PartnerName;
            }
        }

        // Notification Email
        const controlNotificationEmail = this.reactiveFormGroup().get('notificationemail');
        if (controlNotificationEmail) {
            const notificationEmail = controlNotificationEmail.value as string;
            updatedOnboarding.NotificationEmailAddress = notificationEmail;
        }

        //Supported Transports
        const controlSupportedTransports = this.reactiveFormGroup().get('supportedTransports');
        if (controlSupportedTransports) {
            const supportedTransports = controlSupportedTransports.value as Transport[];
            updatedOnboarding.Data.Input.SupportedTransports = supportedTransports;
        }

        return updatedOnboarding;
    }

    loadDocumentTypeFormValues(direction: string, onboardingDocType: PartnerOnboardingDocumentType[]): DocumentTypeInput[] {
        const documentTypeInputs: DocumentTypeInput[] = [];

        onboardingDocType.forEach(docType => {
            const controlNameCheckbox = `doctype${direction}checkbox_${docType.DocumentType.Id}`;
            const controlCheckbox = this.reactiveFormGroup().get(controlNameCheckbox);

            // Only include if checkbox is checked
            if (controlCheckbox && controlCheckbox.value === true) {
                const controlNameFormat = `doctype${direction}format_${docType.DocumentType.Id}`;
                const controlFormat = this.reactiveFormGroup().get(controlNameFormat);

                const controlNameDisplayName = `doctype${direction}displayname_${docType.DocumentType.Id}`;
                const controlDisplayName = this.reactiveFormGroup().get(controlNameDisplayName);

                const formats = controlFormat ? controlFormat.value as number[] : [];
                const displayName = controlDisplayName ? controlDisplayName.value as string : docType.DocumentType.Name;

                documentTypeInputs.push({
                    Name: displayName,
                    DisplayName: displayName,
                    Id: docType.DocumentType.Id,
                    Formats: this.formats().filter(format => formats.includes(format.FormatsId)).map(format => ({
                        Name: format.FormatName,
                        Id: format.FormatsId
                    }))
                } as DocumentTypeInput);
            }
        });

        return documentTypeInputs;
    }

    openPartnerPortal() {
        void this.router.navigate(['partnerportal', { partnerId: this.partnerOnboarding().PartnerId }]);
    }

    editMailTemplate() {
        void this.router.navigate(['settings/mail-templates/details', { mailTemplateKey: 'OnboardingPartnerSendInvitation' }]);
    }

    async sendToPartner() {
        const modalSettings = new ModalSettingsTemplate('lg', 'static');
        const result = await this.modalService.openComponentWithInitialize<PartnerOnboardingSendInvitationDialogResult, PartnerOnboardingSendInvitationDialogComponent>(PartnerOnboardingSendInvitationDialogComponent, modalSettings, dialog => {
              dialog.hasEditMailTemplatePermission.set(this.hasEditMailTemplatePermission());
            });
        if (result === PartnerOnboardingSendInvitationDialogResult.GoToMailTemplate) {
            this.editMailTemplate();
        }
        else if (result === PartnerOnboardingSendInvitationDialogResult.Send) {
            await this.partnerOnboardingService.sendUrlToPartner(this.partnerOnboarding());
            await this.saveLogEvent('CustomerSend');
        }
    }

    async saveAndSendToPartner() {
        const controlEmail = this.reactiveFormGroup().get('email');
        if (controlEmail) {
            const emailValue = controlEmail.value as string;
            if (this.partnerOnboarding().Data.Output.Contact.Email) {
                this.partnerOnboarding().Data.Output.Contact.Email.ContactEmail = emailValue;
            }
            else {
                this.partnerOnboarding().Data.Output.Contact.Email = new PartnerContactEmail({ContactEmail: emailValue, Status: new PartnerOnboardingItemStatus({Status: ItemStatus.Open, CreatedEntities: []})});
            }

            await this.modalService
                .confirmDirect(
                    'Save partner and send to Partner',
                    `Do you want to save the Partner and immediately send a mail to the partner (${emailValue}) with a link to the Partner Portal, using the standard template.?`,
                    'Save and Send Invitation'
                )
                .then(async () => {
                    await this.handleSaveAndSend();
                })
                .catch(() => {
                    console.log('User canceled the dialog');
                });
        }
    }

    private async handleSaveAndSend(): Promise<void> {
        await this.reactiveFormSubmit();
        if (this.partnerOnboarding().PartnerId !== null && this.partnerOnboarding().PartnerId > 0) {
            await this.partnerOnboardingService.sendUrlToPartner(this.partnerOnboarding());
            await this.saveLogEvent('CustomerSend');
            this.toastService.showToast('Partner saved and mail sent to partner', ToastType.success);
        }
    }

    async saveLogEvent(type: string) {
        const event = {
            Id: null,
            PartnerOnboardingId: this.partnerOnboarding().Id,
            Type: type} as PartnerOnboardingLogEvent;
        await this.partnerOnboardingService.logEvent(event);
    }

    async saveLogEventPartnerOnboardingId(type: string, partnerOnboardingId: number) {
        const event = {
            Id: null,
            PartnerOnboardingId: partnerOnboardingId,
            Type: type} as PartnerOnboardingLogEvent;
        await this.partnerOnboardingService.logEvent(event);
    }

    getFormControlValue(identifier: string): string {
        const control = this.reactiveFormGroup().get(identifier);
        if (control) {
            return control.value as string;
        }
        return null;
    }

    isPartnerNameUnique: UniqueValidatorFn = async partnerName =>
        partnerName?.length ? await this.partnerService.isPartnerNameAvailable(partnerName) : true;

    async saveOnboardingPreset() {
        await this.savePresetInternal(false); // false = normal save behavior
    }

    async saveAsOnboardingPreset() {
        await this.savePresetInternal(true); // true = force save as new preset
    }

    private async savePresetInternal(forceSaveAs: boolean = false): Promise<void> {
        try {
            const formData = this.loadFormValues();

            const partnerOnboardingData = {
                Input: formData.Data.Input,
                Output: this.createNewParterDataOutput()
            } as PartnerOnboardingData;

            let presetName: string | null = null;
            const presetsControl = this.reactiveFormGroup().get('onboardingPresetsControl') as AbstractControl<string | null>;
            const selectedPresetId = presetsControl?.value;

            const hasSelectedPreset = selectedPresetId !== null && selectedPresetId !== undefined && selectedPresetId !== '';

            if (forceSaveAs || !hasSelectedPreset) {
                const modalSettings = new ModalSettingsTemplate('md', 'static');

                try {
                    const existingNames = this.onboardingPresets().map(preset => preset.Name);

                    presetName = await this.modalService.openComponentWithInitialize<string, PresetNameDialogComponent>(
                        PresetNameDialogComponent,
                        modalSettings,
                        (dialogComponent) => {
                            dialogComponent.setExistingNames(existingNames);
                        }
                    );

                } catch {
                    console.log('User cancelled preset name dialog');
                    return;
                }

                if (!presetName) {
                    return;
                }

                const newPreset = { Name: presetName, Data: partnerOnboardingData } as PartnerOnboardingPreset;
                const savedPreset = await this.partnerOnboardingService.savePartnerOnboardingPreset(newPreset);
                this.selectedOnboardingPreset.set(savedPreset);

                const updatedOnboarding = structuredClone(this.partnerOnboarding());
                updatedOnboarding.Data.Input = savedPreset.Data.Input;
                this.partnerOnboarding.set(updatedOnboarding);

                this.pendingPresetId.set(savedPreset.Id);
                this.partnerOnboardingPresetsResource.reload();


            } else {
                // Update existing preset
                const currentPreset = this.selectedOnboardingPreset();
                if (currentPreset) {
                    const updatedPreset = { ...currentPreset, Data: partnerOnboardingData };
                    const savedPreset = await this.partnerOnboardingService.savePartnerOnboardingPreset(updatedPreset);
                    this.selectedOnboardingPreset.set(savedPreset);

                    const updatedOnboarding = structuredClone(this.partnerOnboarding());
                    updatedOnboarding.Data.Input = savedPreset.Data.Input;
                    this.partnerOnboarding.set(updatedOnboarding);
                    this.partnerOnboardingPresetsResource.reload();
                }
            }

            this.toastService.showToast('Preset saved successfully!', ToastType.success);

        } catch {
            this.toastService.showToast('Failed to save preset!', ToastType.error);
        } finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    private handlePendingPresetSelection(): void {
        const pendingId = this.pendingPresetId();
        const presets = this.onboardingPresets();

        if (!pendingId || !presets || presets.length === 0) { return;}

        const foundPreset = presets.find(preset => preset.Id === pendingId);
        if (!foundPreset) { return; }

        this.pendingPresetId.set(null);

        const presetsControl = this.reactiveFormGroup().get('onboardingPresetsControl');
        if (presetsControl) {
            presetsControl.patchValue(foundPreset.Id);
        }
    }

    onPresetChange(key: unknown) {
        const presetsControl = this.reactiveFormGroup().get('onboardingPresetsControl');
        if (presetsControl) {
            presetsControl.patchValue(key as number);
        }

        const selectedPreset = this.onboardingPresets().find(preset => preset.Id === key) || null;
        this.selectedOnboardingPreset.set(selectedPreset);

        const currentPreset = this.selectedOnboardingPreset();
        if (currentPreset && currentPreset.Data) {
            const updatedOnboarding = structuredClone(this.partnerOnboarding());
            updatedOnboarding.Data.Input = currentPreset.Data.Input;
            updatedOnboarding.Data.Output.Contact.Types = currentPreset.Data.Input.DefaultContactTypes;
            this.partnerOnboarding.set(updatedOnboarding);
        }
    }

    async managePresets(): Promise<void> {
        try {
            const modalSettings = new ModalSettingsTemplate('lg', 'static');

            const result = await this.modalService.openComponent(
                PresetManagementDialogComponent,
                modalSettings
            ) as PresetManagementResult;

            if (result === PresetManagementResult.PresetsModified) {
                this.partnerOnboardingPresetsResource.reload();
                this.toastService.showToast('Presets updated successfully!', ToastType.success);
            }

        } catch (error) {
            if (error !== 'dismissed') {
                console.error('Error managing presets:', error);
            }
        }
    }
}
